import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LaunchOutlinedIcon from '@mui/icons-material/LaunchOutlined';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import MarkEmailUnreadOutlinedIcon from '@mui/icons-material/MarkEmailUnreadOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha, type Theme } from '@mui/material/styles';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { memo, useState, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { NotificationRecord } from '@/notifications';
import type { NotificationSeverity } from '@/notifications';

interface NotificationCardProps {
  notification: NotificationRecord;
  onMarkRead: (id: string) => void;
  onMarkUnread: (id: string) => void;
  onArchive: (id: string) => void;
  onDelete: (id: string) => void;
  onOpenRelatedProduct: (notification: NotificationRecord) => void;
  onViewDetails: (notification: NotificationRecord) => void;
}

function formatRelativeTime(isoValue: string, t: TFunction): string {
  const createdAt = new Date(isoValue).getTime();
  const diffMs = Date.now() - createdAt;
  const diffMinutes = Math.floor(diffMs / 60_000);

  if (diffMinutes < 1) {
    return t('notification.notificationJustNow');
  }

  if (diffMinutes < 60) {
    return t('notification.notificationMinutesAgoLong', { count: diffMinutes });
  }

  const diffHours = Math.floor(diffMinutes / 60);

  if (diffHours < 24) {
    return t('notification.notificationHoursAgoLong', { count: diffHours });
  }

  const diffDays = Math.floor(diffHours / 24);

  if (diffDays < 7) {
    return t('notification.notificationDaysAgoLong', { count: diffDays });
  }

  return new Date(isoValue).toLocaleDateString();
}

const severityTone: Record<
  NotificationSeverity,
  'error' | 'warning' | 'info' | 'success'
> = {
  critical: 'error',
  warning: 'warning',
  info: 'info',
  success: 'success',
};

const severityLabelKeys: Record<NotificationSeverity, string> = {
  critical: 'notification.notificationSeverityCritical',
  warning: 'notification.notificationSeverityWarning',
  info: 'notification.notificationSeverityInfo',
  success: 'notification.notificationSeveritySuccess',
};

function resolveSeverityTone(
  theme: Theme,
  severity: NotificationSeverity,
): string {
  const tone = severityTone[severity];
  return theme.palette[tone].main;
}

export const NotificationCard = memo(function NotificationCard({
  notification,
  onMarkRead,
  onMarkUnread,
  onArchive,
  onDelete,
  onOpenRelatedProduct,
  onViewDetails,
}: NotificationCardProps) {
  const { t } = useTranslation('entity-portal');
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);

  const handleOpenMenu = (event: MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.75,
        mb: 1.25,
        borderRadius: 1.25,
        borderColor: 'divider',
        bgcolor: 'background.paper',
        borderLeft: '3px solid',
        borderLeftColor: (theme) =>
          resolveSeverityTone(theme, notification.severity),
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        spacing={1}
        alignItems="flex-start"
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ mb: 0.9 }}
          >
            <Chip
              size="small"
              label={t(severityLabelKeys[notification.severity])}
              sx={{
                height: 20,
                color: (theme) =>
                  resolveSeverityTone(theme, notification.severity),
                bgcolor: (theme) =>
                  alpha(
                    resolveSeverityTone(theme, notification.severity),
                    theme.palette.mode === 'dark' ? 0.24 : 0.12,
                  ),
              }}
            />
            <Typography variant="tableHeader" color="text.secondary">
              {notification.productName}
            </Typography>
          </Stack>

          <Typography variant="subtitle1">{notification.title}</Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.2 }}>
            {notification.message}
          </Typography>

          <Stack direction="row" spacing={0.75} alignItems="center">
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                if (notification.read) {
                  onMarkUnread(notification.id);
                  return;
                }

                onMarkRead(notification.id);
              }}
              sx={{
                minWidth: 72,
                px: 1,
                py: 0.35,
                borderColor: 'divider',
                color: 'text.secondary',
                textTransform: 'none',
              }}
            >
              {notification.read
                ? t('notificationCenter.markUnread')
                : t('notificationCenter.markRead')}
            </Button>
            <Button
              size="small"
              variant="text"
              onClick={() => onDelete(notification.id)}
              sx={{
                minWidth: 52,
                px: 0.8,
                color: 'text.secondary',
                textTransform: 'none',
              }}
            >
              {t('notificationCenter.dismiss')}
            </Button>
          </Stack>
        </Box>

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ whiteSpace: 'nowrap', pt: 0.2 }}
        >
          {formatRelativeTime(notification.createdAt, t)}
        </Typography>

        <Tooltip title={t('notificationCenter.actionsLabel')}>
          <IconButton
            onClick={handleOpenMenu}
            aria-label={t('notificationCenter.actionsLabel')}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>

      <Divider sx={{ mt: 1.2, opacity: 0 }} />

      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleCloseMenu}
      >
        {notification.read ? (
          <MenuItem
            onClick={() => {
              onMarkUnread(notification.id);
              handleCloseMenu();
            }}
          >
            <MarkEmailUnreadOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
            {t('notificationCenter.markUnread')}
          </MenuItem>
        ) : (
          <MenuItem
            onClick={() => {
              onMarkRead(notification.id);
              handleCloseMenu();
            }}
          >
            <MarkEmailReadOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
            {t('notificationCenter.markRead')}
          </MenuItem>
        )}

        <MenuItem
          onClick={() => {
            onArchive(notification.id);
            handleCloseMenu();
          }}
        >
          <ArchiveOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
          {t('notificationCenter.archive')}
        </MenuItem>

        <MenuItem
          onClick={() => {
            onDelete(notification.id);
            handleCloseMenu();
          }}
        >
          <DeleteOutlineIcon fontSize="small" sx={{ mr: 1 }} />
          {t('notificationCenter.delete')}
        </MenuItem>

        <MenuItem
          onClick={() => {
            onOpenRelatedProduct(notification);
            handleCloseMenu();
          }}
        >
          <LaunchOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
          {t('notificationCenter.openRelatedProduct')}
        </MenuItem>

        <MenuItem
          onClick={() => {
            onViewDetails(notification);
            handleCloseMenu();
          }}
        >
          <VisibilityOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
          {t('notificationCenter.viewDetails')}
        </MenuItem>

        <MenuItem disabled>
          <ContentCopyOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
          {t('notificationCenter.copyLinkFuture')}
        </MenuItem>
      </Menu>
    </Paper>
  );
});
